<script setup>
// UMA NOTIFICAÇÃO — a mesma peça no sino e na página (07/10/2026).
//
// O sino e a página tinham cada um a sua linha, desenhadas à parte. Aqui:
// selo com o tom da família (o olho separa assunto sem ler), título em até
// duas linhas, o corpo numa linha (só na página), tipo · hora, e as ações À
// VISTA — "marcar como lida" não pode ser segredo atrás do cursor.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { dynamicTime, messageStamp } from 'shared/helpers/timeHelper';
import { iconeDoTipo, tomDoTipo } from '../tiposDeNotificacao';
import { chaveDoDia } from '../agruparPorDia';

const props = defineProps({
  notificacao: { type: Object, required: true },
  compacto: { type: Boolean, default: false },
});
const emit = defineEmits(['abrir', 'lida', 'naoLida']);
const { t } = useI18n();

const n = computed(() => props.notificacao);
const nova = computed(() => !n.value.read_at);
// Hoje mostra a hora ("14:32"); antes disso, o "há 3 dias" de sempre.
const quando = computed(() =>
  chaveDoDia(n.value.last_activity_at) === 'hoje'
    ? messageStamp(n.value.last_activity_at, 'HH:mm')
    : dynamicTime(n.value.last_activity_at)
);
const corpo = computed(() =>
  (n.value.push_message_body || '').replace(/\s+/g, ' ').trim()
);
</script>

<template>
  <li
    class="ntf"
    :class="[
      `ntf--${tomDoTipo(n.notification_type)}`,
      { 'ntf--nova': nova, 'ntf--compacto': compacto },
    ]"
  >
    <button type="button" class="ntf__corpo" @click="emit('abrir', n)">
      <span class="ntf__ico" aria-hidden="true">
        <span :class="iconeDoTipo(n.notification_type)" />
      </span>
      <span class="ntf__texto">
        <span class="ntf__tit">{{ n.push_message_title }}</span>
        <span v-if="!compacto && corpo" class="ntf__resumo">{{ corpo }}</span>
        <span class="ntf__meta">
          <span class="ntf__tipo">
            {{ t(`NOTIFICATIONS_PAGE.TYPE_LABEL.${n.notification_type}`) }}
          </span>
          <span class="ntf__sep" aria-hidden="true" />
          <span>{{ quando }}</span>
        </span>
      </span>
    </button>
    <button
      v-if="nova"
      v-tooltip.left="t('NOTIFICATIONS_PAGE.CENTRAL.MARCAR_LIDA')"
      type="button"
      class="ntf__acao"
      :aria-label="t('NOTIFICATIONS_PAGE.CENTRAL.MARCAR_LIDA')"
      @click="emit('lida', n)"
    >
      <i class="i-ph-check-bold ntf__check" aria-hidden="true" />
    </button>
    <button
      v-else-if="!compacto"
      v-tooltip.left="t('NOTIFICATIONS_PAGE.CENTRAL.MARCAR_NAO_LIDA')"
      type="button"
      class="ntf__acao ntf__acao--lida"
      :aria-label="t('NOTIFICATIONS_PAGE.CENTRAL.MARCAR_NAO_LIDA')"
      @click="emit('naoLida', n)"
    >
      <i class="i-ph-envelope-simple" aria-hidden="true" />
    </button>
  </li>
</template>

<style scoped>
.ntf {
  position: relative;
  display: flex;
  align-items: stretch;
  border-radius: var(--r-4, 12px);
  transition: background var(--mov-1, 180ms) var(--mov-curva, ease);
}
.ntf:hover {
  background: rgb(var(--slate-2));
}
/* A faixa na aresta só nas não lidas: é o que separa "novo" de "já visto"
   sem pintar o cartão inteiro. */
.ntf::before {
  content: '';
  position: absolute;
  inset-block: 10px;
  left: 0;
  width: 3px;
  border-radius: 3px;
  background: var(--ntf-tom);
  transform: scaleY(0);
  transition: transform 240ms cubic-bezier(0.2, 0.8, 0.2, 1);
}
.ntf--nova::before {
  transform: scaleY(1);
}
.ntf__corpo {
  display: flex;
  flex: 1;
  min-width: 0;
  gap: var(--esp-3, 12px);
  padding: var(--esp-3, 12px) var(--esp-2, 8px) var(--esp-3, 12px)
    var(--esp-4, 16px);
  text-align: left;
  background: transparent;
  border: 0;
  cursor: pointer;
}
.ntf__ico {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: var(--ntf-fundo);
  color: var(--ntf-tom);
}
.ntf__ico > span {
  display: block;
  width: 16px;
  height: 16px;
}
.ntf__texto {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.ntf__tit {
  font-size: var(--txt-dado, 14px);
  font-weight: 500;
  line-height: 1.35;
  color: rgb(var(--slate-12));
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}
.ntf--nova .ntf__tit {
  font-weight: 600;
}
.ntf__resumo {
  font-size: var(--txt-nota, 12.5px);
  color: rgb(var(--slate-11));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ntf__meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  font-size: var(--txt-selo, 11.5px);
  color: rgb(var(--slate-10));
  font-variant-numeric: tabular-nums;
}
.ntf__tipo {
  font-weight: 600;
  color: var(--ntf-tom);
  white-space: nowrap;
}
.ntf__sep {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: currentcolor;
  opacity: 0.5;
}
/* A ação À VISTA: o visto aparece sempre, discreto em repouso e cheio no
   cursor — "marcar como lida" não pode morar só no hover (regra do padrão).
   O "nova" já é dito pela faixa na aresta e pelo título em negrito. */
.ntf__acao {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 40px;
  padding: 0;
  margin: 6px 6px 6px 0;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--ntf-tom);
  cursor: pointer;
  opacity: 0.6;
  transition:
    background var(--mov-1, 180ms) var(--mov-curva, ease),
    opacity var(--mov-1, 180ms) var(--mov-curva, ease);
}
.ntf:hover .ntf__acao,
.ntf__acao:focus-visible {
  opacity: 1;
}
.ntf__acao:hover,
.ntf__acao:focus-visible {
  background: var(--ntf-fundo);
}
.ntf__check {
  display: block;
  width: 16px;
  height: 16px;
}
.ntf__acao--lida {
  color: rgb(var(--slate-10));
  opacity: 0.55;
}
.ntf:hover .ntf__acao--lida {
  opacity: 1;
}
.ntf__acao--lida > i {
  width: 16px;
  height: 16px;
}

/* Sino: mais justo. */
.ntf--compacto .ntf__corpo {
  gap: 10px;
  padding: 10px 6px 10px 14px;
}
.ntf--compacto .ntf__ico {
  width: 30px;
  height: 30px;
  border-radius: 9px;
}
.ntf--compacto .ntf__ico > span {
  width: 14px;
  height: 14px;
}
.ntf--compacto .ntf__tit {
  font-size: 13px;
}
.ntf--compacto .ntf__acao {
  width: 34px;
}

/* ── Tons por família (os do produto) ───────────────────────────────── */
.ntf--azul {
  --ntf-tom: rgb(var(--blue-11));
  --ntf-fundo: rgb(var(--blue-3));
}
.ntf--teal {
  --ntf-tom: rgb(var(--teal-11));
  --ntf-fundo: rgb(var(--teal-3));
}
.ntf--iris {
  --ntf-tom: rgb(var(--iris-11));
  --ntf-fundo: rgb(var(--iris-3));
}
.ntf--violeta {
  --ntf-tom: rgb(var(--violet-11));
  --ntf-fundo: rgb(var(--violet-3));
}
.ntf--ambar {
  --ntf-tom: rgb(var(--amber-11));
  --ntf-fundo: rgb(var(--amber-3));
}
.ntf--rubi {
  --ntf-tom: rgb(var(--ruby-11));
  --ntf-fundo: rgb(var(--ruby-3));
}
.ntf--slate {
  --ntf-tom: rgb(var(--slate-11));
  --ntf-fundo: rgb(var(--slate-3));
}
@media (prefers-reduced-motion: reduce) {
  .ntf,
  .ntf::before {
    transition: none;
  }
}
</style>
